import { Controller } from "@hotwired/stimulus"
import { interpolate } from "../../utils/addon-cost-line.util"
import { getCurrencyDecimals } from "../../utils/number-format.util"
import { formatMoney, formatPercent, serverNumberFormatFor } from "../../utils/server-format.util"
import {
  deriveAdvanceDeductions,
  isCustomOption,
  offsetAdvances,
  optionPercent,
  parseCustomPercent,
  summarize
} from "../../utils/vat-deduction.util"

// Connects to data-controller="expenses--vat-deduction" (section#expense_items_section)
// Odpočet DPH on the expense form: the document select in the summary, the
// Odpočet column and custom-% row per line, and the deduction grid. The line VAT
// comes from documents--totals-calculator#getLineTaxes, so the grid reconciles
// with the VAT breakdown above it and with the server's calculator. Everything
// written here is server-rendered first (VatDeductionHelper) with the same
// figures, so a refresh on load changes nothing.
//
// A method's percent is its <option>'s: data-percent for a fixed method,
// data-custom="true" for one whose percent is typed into the custom-% field
// (VatDeductionHelper#vat_deduction_option). An option with neither — the
// prompt, "Z dokladu" — has no value of its own, so the line inherits. The
// document select's blank option is the default where the deduction applies
// (VatDeductionHelper#vat_deduction_blank_option): value "" (nothing is
// posted), but data-percent and data-default-method like a fixed method.
//
// On an expense that settles an advance (settlesAdvances, R55 1a) a negative
// line is the advance's deduction and takes the other lines' blended percent
// (deriveAdvanceDeductions). Its select shows that state disabled, so its own
// method is neither posted nor cleared. The server renders that for the saved
// lines, with the stored method in data-advance-restore; a line that turns
// negative here is put the same way. Either gets its own choice back when it
// stops being one.
//
// On a final whose advance's DPP already claimed the VAT (the grid's
// data-advance-claimed), the grid nets what the deduction lines do not take
// back (offsetAdvances), unless "Faktúra len na zvyšok ceny" (remainderOnly)
// is ticked.
//
// Foreign VAT is a cost, not a deduction (VatDeductionHelper#foreign_vat_expense?):
// the server renders a note and one row, the expense's whole VAT, in the grid and
// keeps the deduction amount rows hidden and empty; it renders no document select,
// hint or unset warning, and the lines' Odpočet cells say "Zahraničná DPH" instead
// of their select. The form's foreign flag tells this controller to leave all that
// so, on a line added on the open form too (its select hidden, the label shown),
// and to keep the foreign row's amount current (the lines' VAT, as
// Calculator#vat_total). The flag is what the server found at render; the rule
// itself (BasisCheck) is not replayed here.
export default class extends Controller {
  static outlets = ["documents--totals-calculator"]

  static targets = [
    "documentMethod",
    "documentPercent",
    "hint",
    "unsetAlert",
    "lineMethod",
    "lineRow",
    "linePercent",
    "lineReason",
    "lineResult",
    "lineForeign",
    "grid",
    "deductible",
    "nonDeductible",
    "unset",
    "unsetRow",
    "foreignAmount",
    "remainderOnly"
  ]

  static values = {
    texts: { type: Object, default: {} },
    settlesAdvances: { type: Boolean, default: false },
    foreign: { type: Boolean, default: false }
  }

  #frame = null

  // This section connects before the item rows and the summary inside it, and
  // an item reports nothing until its own controller is up: the first refresh
  // waits a frame, as documents--item's first totals notification does.
  connect() {
    this.#scheduleRefresh()
  }

  documentsTotalsCalculatorOutletConnected() {
    this.#scheduleRefresh()
  }

  // An appended item row ("Pridaj položku") renders its inherit option before
  // the document's value is known to the server: relabel it on the next frame.
  lineMethodTargetConnected() {
    this.#scheduleRefresh()
  }

  disconnect() {
    if (this.#frame) cancelAnimationFrame(this.#frame)
    this.#frame = null
  }

  onDocumentChange() {
    // A leftover fixed percent must not be posted under a custom method, and a
    // percent without a method is refused: always start from an empty field.
    this.documentPercentTarget.value = ""
    this.documentPercentTarget.classList.remove("is-invalid")
    this.refresh()
    if (this.#isCustom(this.documentMethodTarget)) this.documentPercentTarget.focus()
  }

  onDocumentPercent() {
    this.refresh()
  }

  onLineMethod(event) {
    const index = event.target.dataset.index
    const rows = this.#lineRows()
    const percent = this.#linePercentFor(index, rows)
    if (percent) {
      percent.value = ""
      percent.classList.remove("is-invalid")
    }
    const custom = this.#isCustom(event.target)
    if (!custom) this.#clearReason(index, rows)
    this.refresh()
    if (custom) percent?.focus()
  }

  onLinePercent() {
    this.refresh()
  }

  resetLine(event) {
    const index = event.currentTarget.dataset.index
    const rows = this.#lineRows()
    const select = this.lineMethodTargets.find((el) => el.dataset.index === index)
    if (select) select.value = ""
    const percent = this.#linePercentFor(index, rows)
    if (percent) {
      percent.value = ""
      percent.classList.remove("is-invalid")
    }
    this.#clearReason(index, rows)
    this.refresh()
    // The button just hid with its row: hand focus back to the line's select.
    select?.focus()
  }

  // Public API: called by documents--totals-calculator after every
  // recalculation. Every write is idempotent, so it runs on each keystroke.
  refresh() {
    // A foreign expense has no document select to read, and still has its lines and
    // the foreign amount to keep.
    if (!this.hasDocumentsTotalsCalculatorOutlet || !(this.hasDocumentMethodTarget || this.foreignValue)) return

    const calculator = this.documentsTotalsCalculatorOutlet
    const currency = calculator.getCurrency()
    const decimals = getCurrencyDecimals(currency)
    const numberFormat = serverNumberFormatFor(this.element)
    const format = {
      money: (value) => formatMoney(value, currency, numberFormat),
      percent: (value) => formatPercent(value, numberFormat || {}, { maximumFractionDigits: 0 })
    }
    // Both read once: a Stimulus Object value parses its JSON on every access,
    // and the line rows are looked up per line below.
    const texts = this.textsValue
    const rows = this.#lineRows()

    const documentOption = this.hasDocumentMethodTarget ? this.#selectedOption(this.documentMethodTarget) : null
    const documentPercent = this.#documentPercent(documentOption)

    let overrides = 0
    let lines = calculator.getLineTaxes()
    const derivedLabels = new Map() // select → the advance-deduction label
    if (lines.length === 0) {
      // No item rows: the calculator's per-rate pseudo-lines, all at the document value.
      lines = this.#pseudoLineTaxes().map(({ rate, tax }) => ({ tax, rate, percent: documentPercent }))
    } else {
      lines = lines.map(({ row, tax, rate, net }) => {
        const select = row.querySelector("[data-expenses--vat-deduction-target~='lineMethod']")
        const ownOption = this.#ownOption(select)
        const percent = ownOption
          ? this.#percentOf(ownOption, this.#linePercentFor(select.dataset.index, rows))
          : documentPercent
        return { select, ownOption, tax, rate, net, percent }
      })
      lines = deriveAdvanceDeductions(lines, this.settlesAdvancesValue, decimals)
      for (const { select, ownOption, tax, percent, derived } of lines) {
        if (select) this.#writeAdvanceState(select, derived)
        if (derived) {
          derivedLabels.set(select, this.#advanceLabel(percent, texts, format))
          this.#lineResultFor(select?.dataset.index, rows)?.replaceChildren()
        } else if (ownOption) {
          overrides += 1
          this.#writeLineResult(this.#lineResultFor(select.dataset.index, rows), ownOption, tax, percent, decimals, {
            texts,
            format
          })
        }
      }
    }

    const defaultMethod = documentOption?.value ? undefined : documentOption?.dataset.defaultMethod
    const inheritLabel = this.#inheritLabel(documentOption?.value || defaultMethod, documentPercent, texts, format)
    this.#writeLineSelects(inheritLabel, derivedLabels, rows, texts, format)
    this.#writeForeignLabels()
    const counted = offsetAdvances(lines, this.#advanceClaimed(), decimals)
    this.#writeGrid(
      summarize(counted, decimals),
      counted.some(({ percent }) => percent !== null),
      this.#vatTotal(counted, decimals),
      format
    )
    this.#writeHint(documentPercent !== null, Boolean(defaultMethod), overrides, lines.length, texts)
  }

  #scheduleRefresh() {
    if (this.#frame) cancelAnimationFrame(this.#frame)
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null
      this.refresh()
    })
  }

  // The document percent field exists (and is submitted) only for a custom method.
  #documentPercent(option) {
    const input = this.hasDocumentPercentTarget ? this.documentPercentTarget : null
    if (input) {
      const custom = isCustomOption(option?.dataset)
      input.classList.toggle("d-none", !custom)
      input.disabled = !custom
    }
    return this.#percentOf(option, input)
  }

  // A method option's percent; for a custom one the typed value, marked
  // is-invalid when the server would refuse it (and then counted as unset). A
  // blank field is unset without a client mark — a server error on it stays shown.
  #percentOf(option, input) {
    if (!isCustomOption(option?.dataset) || !input) return optionPercent(option?.dataset, input?.value)

    const { percent, invalid } = parseCustomPercent(input.value)
    if (invalid) input.classList.add("is-invalid")
    else if (percent !== null) input.classList.remove("is-invalid")
    return percent
  }

  #inheritLabel(method, percent, texts, format) {
    const short =
      percent === null ? texts.unset : interpolate(texts.short?.[method] ?? "", { percent: format.percent(percent) })
    return `${texts.inheritPrefix || ""}${short || ""}`
  }

  // Inherit option label (an advance-deduction line's derived label instead),
  // the custom option's percent, own/inherited colour,
  // the custom row's visibility and its inputs' enabled state — for every line.
  // A non-payer supplier hides the row (documents--vat-payer-toggle rewrites the
  // calculator's value, then recalculates, which calls refresh) but leaves its
  // inputs as they are: the line's own setting is still submitted.
  #writeLineSelects(inheritLabel, derivedLabels, rows, texts, format) {
    const payer = this.documentsTotalsCalculatorOutlet.isVatPayerValue
    for (const select of this.lineMethodTargets) {
      const index = select.dataset.index
      const own = this.#hasOwnValue(this.#selectedOption(select))
      const custom = this.#isCustom(select)
      const label = derivedLabels.get(select) ?? inheritLabel
      const inheritOption = select.querySelector("option[value='']")
      if (inheritOption && inheritOption.textContent !== label) inheritOption.textContent = label

      const customOption = select.querySelector("option[data-custom='true']")
      if (customOption) {
        const { percent } = parseCustomPercent(this.#linePercentFor(index, rows)?.value)
        customOption.textContent =
          custom && percent !== null
            ? interpolate(texts.customWith, { percent: format.percent(percent) })
            : texts.custom || customOption.textContent
      }

      select.classList.toggle("invoice-item__deduction-select--own", own)
      select.classList.toggle("invoice-item__deduction-select--inherited", !own)
      // Foreign VAT: the cell says so (below) instead of offering the select. A hidden
      // select still posts the method it holds, the saved one.
      select.classList.toggle("d-none", this.foreignValue)

      const row = rows.get(index)
      row?.classList.toggle("d-none", !(custom && payer && !this.foreignValue && this.#itemAlive(index)))
      for (const input of row?.querySelectorAll("input") || []) input.disabled = !custom
      if (!custom) this.#lineResultFor(index, rows)?.replaceChildren()
    }
  }

  // "Zahraničná DPH" in the Odpočet cell of every line, foreign VAT only.
  #writeForeignLabels() {
    for (const label of this.lineForeignTargets) label.classList.toggle("d-none", !this.foreignValue)
  }

  #selectedOption(select) {
    return select?.selectedOptions[0] ?? null
  }

  // The line's own method option: what its select holds, or — while this
  // controller has disabled it as an advance-deduction line — the choice it put
  // aside. Null when that option has no value of its own (inherit).
  #ownOption(select) {
    if (!select) return null
    const method = select.dataset.advanceRestore ?? select.value
    const option = Array.from(select.options).find((candidate) => candidate.value === method)
    return this.#hasOwnValue(option) ? option : null
  }

  #hasOwnValue(option) {
    return Boolean(option && (option.dataset.percent !== undefined || isCustomOption(option.dataset)))
  }

  #isCustom(select) {
    return isCustomOption(this.#selectedOption(select)?.dataset)
  }

  // A line that became an advance-deduction line here: put its choice aside,
  // show the inherit option (relabelled) and disable it, as the server renders
  // a derived line (data-advance-restore = its stored method). When it stops
  // being one, hand the choice back.
  #writeAdvanceState(select, derived) {
    const putAside = select.dataset.advanceRestore !== undefined
    if (derived && !putAside) {
      select.dataset.advanceRestore = select.value
      select.value = ""
      select.disabled = true
    } else if (!derived && putAside) {
      select.disabled = false
      select.value = select.dataset.advanceRestore
      delete select.dataset.advanceRestore
    }
  }

  // "Ako ostatné položky · 50 %", or the unset variant (VatDeductionHelper#vat_deduction_derived_label).
  #advanceLabel(percent, texts, format) {
    if (percent === null) return texts.advanceLineUnset || ""
    return interpolate(texts.advanceLine, { percent: format.percent(percent) })
  }

  #writeLineResult(result, option, tax, percent, decimals, { texts, format }) {
    if (!result) return
    if (!isCustomOption(option.dataset) || percent === null) {
      result.replaceChildren()
      return
    }
    const { deductible } = summarize([{ tax, percent, rate: null }], decimals)
    result.textContent = interpolate(texts.resultOf, {
      deductible: format.money(deductible),
      vat: format.money(tax)
    })
  }

  // Σ of the lines' VAT in minor units, the server's Calculator#vat_total: the
  // offset lines of an advance count, whatever the deduction percent is.
  #vatTotal(lines, decimals) {
    const factor = 10 ** decimals
    return lines.reduce((sum, { tax }) => sum + Math.round(tax * factor), 0) / factor
  }

  #writeGrid(summary, anySet, vatTotal, format) {
    const unset = summary.unsetCount > 0
    // Foreign VAT: the whole VAT is a cost, so that is the one amount; the deduction
    // rows stay hidden and empty, the unset row with them.
    if (this.foreignValue) {
      if (this.hasForeignAmountTarget) this.foreignAmountTarget.textContent = format.money(vatTotal)
    } else {
      if (this.hasDeductibleTarget) this.deductibleTarget.textContent = anySet ? format.money(summary.deductible) : "—"
      if (this.hasNonDeductibleTarget) {
        this.nonDeductibleTarget.textContent = anySet ? format.money(summary.nonDeductible) : "—"
      }
      if (this.hasUnsetTarget) this.unsetTarget.textContent = format.money(summary.unset)
      for (const el of this.unsetRowTargets) el.classList.toggle("d-none", !unset)
    }
    // Foreign VAT is never in the period's VAT, whatever the deduction: nothing is unset.
    if (this.hasUnsetAlertTarget) this.unsetAlertTarget.classList.toggle("d-none", this.foreignValue || !unset)
  }

  // Which lines the document value reaches: all, all but `overrides`, or none.
  // The default reaching every line says what it is (VatDeductionHelper#vat_deduction_hint).
  #writeHint(documentSet, documentDefault, overrides, total, texts) {
    // Foreign VAT is a cost whatever the document value: the hint has nothing to say.
    if (!this.hasHintTarget || this.foreignValue) return
    let hint = ""
    if (documentSet && documentDefault && overrides === 0) hint = texts.hintDefault
    else if (documentSet && overrides === 0) hint = texts.hintAll
    else if (documentSet && overrides < total) hint = interpolate(texts.hintSome, { count: overrides, total })
    else if (documentSet) hint = texts.hintInherit
    if (this.hintTarget.textContent !== (hint || "")) this.hintTarget.textContent = hint || ""
  }

  // [{ rate, tax }] the advance DPPs claimed; none while the final is marked
  // as billed only for the remainder.
  #advanceClaimed() {
    if (!this.hasGridTarget || (this.hasRemainderOnlyTarget && this.remainderOnlyTarget.checked)) return []
    try {
      const claimed = JSON.parse(this.gridTarget.dataset.advanceClaimed || "[]")
      if (!Array.isArray(claimed)) return []
      return claimed.map((entry) => ({ rate: Number(entry?.rate), tax: Number(entry?.tax) || 0 }))
    } catch {
      return []
    }
  }

  // [{ rate, tax }] — rate null for the one "tax amount without breakdown" line.
  #pseudoLineTaxes() {
    if (!this.hasGridTarget) return []
    try {
      const lines = JSON.parse(this.gridTarget.dataset.pseudoLineTaxes || "[]")
      if (!Array.isArray(lines)) return []
      return lines.map((line) => ({
        rate: line?.rate === null || line?.rate === undefined ? null : Number(line.rate),
        tax: Number(line?.tax) || 0
      }))
    } catch {
      return []
    }
  }

  // The item row is still on the form: not removed, not marked for destruction.
  #itemAlive(index) {
    const item = document.getElementById(`invoice_item_${index}`)
    if (!item || item.classList.contains("d-none")) return false
    return item.querySelector('input[type="hidden"][name*="_destroy"]')?.value !== "true"
  }

  // The custom-% rows by their item index, read once per refresh or action.
  #lineRows() {
    return new Map(this.lineRowTargets.map((row) => [row.dataset.index, row]))
  }

  #linePercentFor(index, rows) {
    return rows.get(index)?.querySelector("[data-expenses--vat-deduction-target~='linePercent']")
  }

  #lineResultFor(index, rows) {
    return rows.get(index)?.querySelector("[data-expenses--vat-deduction-target~='lineResult']")
  }

  #clearReason(index, rows) {
    const reason = rows.get(index)?.querySelector("[data-expenses--vat-deduction-target~='lineReason']")
    if (reason) reason.value = ""
  }
}
